import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CrmToaster, crmToast } from './crmUi'
import { useCrm } from './useCrm'
import { apiGet, apiPost } from '../../utils/api'
import { getUsers } from './crmApi'
import { OB, DropMenu, personColor, useObFont } from './obDesign'

/**
 * SESSION CALENDAR — rebuilt to Natalie's Calendar.dc.html mockup (design
 * rebuild 3/3, 30 Oct 2026). Every onboarding call across customers for a
 * week: week nav, stat tiles that filter, Week | List underline tabs, white
 * day columns (today = #EEF1F5 with a 2px ink border — never red), session
 * cards (time strong, company, station, owner avatar, red not-paid flag),
 * and the always-shown "No next call booked" card with Send booking link +
 * Book per customer.
 */

interface Session { id: string; onboardingId: string; leadId: string | null; company: string; startAt: string | null; durationMin: number | null; subject: string | null; stageIndex: number | null; status: string; ownerId: string | null; ownerName?: string | null; notPaid: boolean }
interface TodoRow { key: string; type: string; company: string; leadId: string | null; onboardingId?: string | null; title: string; origin?: string | null }

const mondayOf = (d: Date) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x }
const iso = (d: Date) => d.toISOString().slice(0, 10)

export const CrmObCalendar: React.FC = () => {
  useObFont()
  const navigate = useNavigate()
  const [week, setWeek] = useState(() => mondayOf(new Date()))
  const [view, setView] = useState<'Week' | 'List'>('Week')
  const [tile, setTile] = useState('')
  const [person, setPerson] = useState('Everyone')
  const { data: raw } = useCrm<{ sessions: Session[] }>(() => apiGet<{ sessions: Session[] }>(`/crm/onboarding/calendar?week=${iso(week)}`), { sessions: [] }, [iso(week)])
  const { data: todos } = useCrm<{ rows: TodoRow[] }>(() => apiGet<{ rows: TodoRow[] }>('/crm/onboarding/todos'), { rows: [] })
  const { data: users } = useCrm<any[]>(() => getUsers() as any, [])
  const noCall = todos.rows.filter((r) => r.type === 'Book a call')
  const data = useMemo(() => ({
    sessions: raw.sessions.map((s) => ({ ...s, ownerName: (users || []).find((u: any) => String(u.id) === String(s.ownerId))?.name || s.ownerName || null })),
  }), [raw, users])

  const owners = useMemo(() => [...new Set(data.sessions.map((s) => s.ownerName || s.ownerId).filter(Boolean))] as string[], [data.sessions])
  const sessions = useMemo(() => data.sessions.filter((s) => {
    if (person !== 'Everyone' && String(s.ownerName || s.ownerId || '') !== person) return false
    if (tile === 'Today' && (!s.startAt || new Date(s.startAt).toDateString() !== new Date().toDateString())) return false
    if (tile === 'Not paid' && !s.notPaid) return false
    return true
  }), [data.sessions, person, tile])

  const days = [0, 1, 2, 3, 4].map((i) => new Date(week.getTime() + i * 86400_000))
  const showNoCallOnly = tile === 'No next call'
  const thisMonday = mondayOf(new Date()).getTime()
  const weekLabel = `${week.getTime() === thisMonday ? 'This week' : week.getTime() === thisMonday + 7 * 86400_000 * 1 ? 'Next week' : week.getTime() === thisMonday - 7 * 86400_000 ? 'Last week' : 'Week'} · ${week.toLocaleDateString('en-GB', { day: 'numeric' })}–${new Date(week.getTime() + 4 * 86400_000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`

  const navBtn: React.CSSProperties = { font: 'inherit', fontSize: 13, fontWeight: 700, background: '#fff', color: OB.ink, border: `1px solid ${OB.inputBorder}`, borderRadius: 10, padding: '8px 14px', cursor: 'pointer' }
  const tileDefs: Array<[string, string, number, boolean]> = [
    ['', 'Sessions this week', sessions.length, false],
    ['Today', 'Today', data.sessions.filter((s) => s.startAt && new Date(s.startAt).toDateString() === new Date().toDateString()).length, false],
    ['Not paid', 'Not paid yet', data.sessions.filter((s) => s.notPaid).length, true],
    ['No next call', 'No next call booked', noCall.length, true],
  ]

  const SessionCard: React.FC<{ s: Session }> = ({ s }) => (
    <button onClick={() => navigate(`/crm/onboardings/co/${s.onboardingId}?tab=sessions`)}
      style={{ font: 'inherit', textAlign: 'left', background: '#fff', borderRadius: 10, padding: '9px 10px', display: 'flex', flexDirection: 'column', gap: 5, cursor: 'pointer', border: `1px solid ${s.notPaid ? OB.redBorder : OB.border}` }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 6, fontSize: 12, color: OB.muted }}>
        <strong style={{ color: OB.ink }}>{s.startAt ? new Date(s.startAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—'}</strong>
        {s.durationMin ? <span>{s.durationMin} min</span> : null}
      </span>
      <strong style={{ fontSize: 13, color: OB.ink }}>{s.company}</strong>
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: OB.muted }}>{s.subject || 'Session'}</span>
        {(s.ownerName || s.ownerId) && (
          <span title={s.ownerName || String(s.ownerId)} style={{ width: 24, height: 24, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 800, color: '#fff', flex: '0 0 24px', background: personColor(s.ownerName || String(s.ownerId)) }}>
            {(s.ownerName || '?').slice(0, 2).toUpperCase()}
          </span>
        )}
      </span>
      {s.notPaid && <span style={{ fontSize: 11, fontWeight: 800, color: OB.red }}>Not paid yet</span>}
    </button>
  )

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: OB.pageBg, fontFamily: OB.font, color: OB.ink }}>
      <CrmToaster />
      <div style={{ flex: 'none', padding: '16px 28px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <header style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>Session calendar</h1>
              <span style={{ fontSize: 12.5, color: OB.muted }}>Every onboarding call across customers, and who still needs one booking</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button aria-label="Previous week" style={navBtn} onClick={() => setWeek(new Date(week.getTime() - 7 * 86400_000))}>←</button>
              <strong style={{ fontSize: 14, minWidth: 150, textAlign: 'center' }}>{weekLabel}</strong>
              <button aria-label="Next week" style={navBtn} onClick={() => setWeek(new Date(week.getTime() + 7 * 86400_000))}>→</button>
            </div>
          </header>

          {/* stat tiles */}
          <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            {tileDefs.map(([k, label, n, red]) => {
              const on = tile === k && k !== ''
              return (
                <button key={label} onClick={() => setTile(tile === k ? '' : k)} aria-pressed={on}
                  style={{ font: 'inherit', textAlign: 'left', background: on ? OB.accentPale : '#fff', border: `1px solid ${on ? OB.accent : OB.border}`, borderRadius: 12, padding: '12px 14px', height: 72, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', cursor: 'pointer' }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: red && n > 0 ? OB.red : OB.ink }}>{n}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: OB.muted }}>{label}</span>
                </button>
              )
            })}
          </section>

          {/* tabs + filters */}
          <div role="tablist" style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${OB.border}` }}>
            {(['Week', 'List'] as const).map((v) => (
              <button key={v} role="tab" onClick={() => setView(v)}
                style={{ font: 'inherit', fontSize: 14, background: 'none', border: 0, padding: '10px 14px', minHeight: 44, cursor: 'pointer', ...(view === v ? { fontWeight: 800, color: OB.accent, borderBottom: `3px solid ${OB.accent}` } : { color: OB.muted, borderBottom: '3px solid transparent' }) }}>{v}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <DropMenu name="Person" value={person} active={person !== 'Everyone'}
              options={['Everyone', ...owners].map((l) => ({ label: l, on: person === l, pick: () => setPerson(l) }))} />
            <span style={{ fontSize: 12.5, color: OB.muted, alignSelf: 'center', marginLeft: 'auto' }}>
              {sessions.length} session{sessions.length === 1 ? '' : 's'}{tile && tile !== 'No next call' ? ` · ${tile.toLowerCase()}` : ''}
            </span>
          </div>

      </div>

      {/* grid / list / no-call — scroll under the fixed chrome */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 14, padding: '14px 28px 24px' }}>
          {!showNoCallOnly && view === 'Week' && (
            <section style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 10, minWidth: 940 }}>
              {days.map((d) => {
                const isToday = d.toDateString() === new Date().toDateString()
                const daySessions = sessions.filter((s) => s.startAt && new Date(s.startAt).toDateString() === d.toDateString())
                  .sort((a, b) => new Date(a.startAt!).getTime() - new Date(b.startAt!).getTime())
                return (
                  <div key={iso(d)} style={{ borderRadius: 12, padding: 10, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 360, boxSizing: 'border-box', ...(isToday ? { background: OB.rowDiv, border: `2px solid ${OB.ink}` } : { background: '#fff', border: `1px solid ${OB.border}` }) }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <strong style={{ fontSize: 13.5 }}>{d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</strong>
                      {daySessions.length > 0 && <span style={{ fontSize: 12, color: OB.faint }}>{daySessions.length} session{daySessions.length === 1 ? '' : 's'}</span>}
                    </div>
                    {daySessions.map((s) => <SessionCard key={s.id} s={s} />)}
                    {!daySessions.length && <span style={{ fontSize: 12.5, color: OB.faint }}>No sessions</span>}
                  </div>
                )
              })}
            </section>
          )}

          {/* list view */}
          {!showNoCallOnly && view === 'List' && (
            <section style={{ background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 12, overflow: 'hidden' }}>
              {sessions.sort((a, b) => new Date(a.startAt || 0).getTime() - new Date(b.startAt || 0).getTime()).map((s) => (
                <button key={s.id} className="ob-row" onClick={() => navigate(`/crm/onboardings/co/${s.onboardingId}?tab=sessions`)}
                  style={{ font: 'inherit', width: '100%', display: 'grid', gridTemplateColumns: '110px 80px minmax(0,1.4fr) minmax(0,1.2fr) 160px minmax(0,1fr)', gap: 12, alignItems: 'center', padding: '11px 16px', border: 0, borderBottom: `1px solid ${OB.rowDiv}`, fontSize: 13, color: OB.ink, background: 'none', cursor: 'pointer', textAlign: 'left', boxSizing: 'border-box' }}>
                  <strong>{s.startAt ? new Date(s.startAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '—'}</strong>
                  <span>{s.startAt ? new Date(s.startAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  <strong>{s.company}</strong>
                  <span style={{ fontSize: 11, fontWeight: 700, color: OB.muted }}>{s.subject || 'Session'}</span>
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {(s.ownerName || s.ownerId) && <span style={{ width: 24, height: 24, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 800, color: '#fff', flex: '0 0 24px', background: personColor(s.ownerName || String(s.ownerId)) }}>{(s.ownerName || '?').slice(0, 2).toUpperCase()}</span>}
                    <span>{s.ownerName || ''}</span>
                  </span>
                  {s.notPaid ? <span style={{ fontSize: 11, fontWeight: 800, color: OB.red }}>Not paid yet</span> : <span />}
                </button>
              ))}
              {!sessions.length && <div style={{ padding: '20px 16px', fontSize: 13.5, color: OB.muted }}>No sessions this week.</div>}
            </section>
          )}

          {/* no next call booked — always shown */}
          <section style={{ background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 12, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 15 }}>No next call booked</strong>
              <span style={{ fontSize: 12.5, color: OB.muted }}>{noCall.length} customer{noCall.length === 1 ? '' : 's'} in onboarding with nothing booked</span>
            </div>
            {noCall.map((r) => (
              <div key={r.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1.6fr) auto', gap: 12, alignItems: 'center', padding: '10px 0', borderTop: `1px solid ${OB.rowDiv}`, fontSize: 13 }}>
                <button onClick={() => r.onboardingId ? navigate(`/crm/onboardings/co/${r.onboardingId}`) : r.leadId && navigate(`/crm/lead/${r.leadId}`)}
                  style={{ font: 'inherit', fontWeight: 800, color: OB.ink, background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.company}</button>
                <span style={{ color: OB.muted }}>{r.origin || r.title}</span>
                <span style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => apiPost('/crm/ob-drafts/ensure', { kind: 'booking_link', onboardingId: r.onboardingId || null, leadId: r.leadId, why: 'No next call booked' }).then(async (res: Response) => { const d: any = await res.json(); navigate(`/crm/onboardings/followups?draft=${d.id}`) }).catch(() => crmToast('Could not raise the draft', 'error'))}
                    style={{ font: 'inherit', fontSize: 13, fontWeight: 700, background: '#fff', color: OB.ink, border: `1px solid ${OB.inputBorder}`, borderRadius: 10, padding: '4px 10px', minHeight: 32, cursor: 'pointer' }}>Send booking link</button>
                  <button onClick={() => r.onboardingId && navigate(`/crm/onboardings/co/${r.onboardingId}?tab=sessions`)}
                    style={{ font: 'inherit', fontSize: 13, fontWeight: 800, background: OB.accent, color: '#fff', border: 0, borderRadius: 10, padding: '4px 10px', minHeight: 32, cursor: 'pointer' }}>Book</button>
                </span>
              </div>
            ))}
            {!noCall.length && <span style={{ fontSize: 12.5, color: OB.faint }}>Everyone live has a next call booked</span>}
          </section>

          <span style={{ fontSize: 12, color: OB.muted }}>Times in UK time. Each session links to that customer's Sessions tab.</span>
      </div>
    </div>
  )
}
export default CrmObCalendar
